import axios from "axios";
import React, { ReactElement, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import Button from "../../Button/Button";
import loginLogo from "../Login/loginLogo.png";
import SendToast from "../../Toast/Toast";
import Text from "../../Text/Text";
import Icon from "../../Icon/Icon";
import RequestAccessOrPermissionModal from "../Login/RequestAccessOrPermissionModal";
import styles from "./VerifyLogin.module.css";

interface LoginResponse {
  session: string;
  userIdForSRP: string;
  username: string;
}

interface VerifyLoginProps {
  session: string;
  loginResponse: LoginResponse;
  railAuthApiUrl: string;
  railAuthGroups?: Array<string>;
  logoStyle?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
  signInButtonStyle?: React.CSSProperties;
  customAxiosInstance?: any;
  firstLogin?: boolean;
  close: Function;
  back: Function;
  verifySetupComplete: Function;
}

interface MFALoginData {
  groups?: Array<string> | string;
  session: string;
  userCode: string;
  userIdForSRP: string;
  username: string;
}

interface VerifyMFAData {
  session: string;
  userCode: string;
}

const VerifyLogin: React.FC<VerifyLoginProps> = ({
  session,
  loginResponse,
  railAuthApiUrl,
  logoStyle = {},
  headerStyle = {},
  signInButtonStyle = {},
  customAxiosInstance,
  firstLogin,
  close,
  back,
  verifySetupComplete,
}): ReactElement => {
  const axiosInstance = customAxiosInstance || axios;
  const history = useHistory();
  const {
    register,
    handleSubmit,
    setError,
    errors,
    formState: { isValid },
  } = useForm({ mode: "onChange" });
  const [
    requestAccessOrPermissionModalIsShown,
    setRequestAccessOrPermissionModalIsShown,
  ] = useState(false);
  const [requestAccessOrPermissionModalType, setRequestAccessOrPermissionModalType] = useState<
    "access" | "permission"
  >("permission");

  const onVerifyError = (error: any) => {
    if (
      error.response.data.message === "Invalid code received for user" ||
      error.response.data.message === "Code mismatch" ||
      error.response.data.message.includes("validation error")
    ) {
      setError("code", "manual", "Passcode incorrect. Please try again.");
    } else if (error.response.data.message.includes("verify their email")) {
      SendToast(
        "Your email address requires verification to access this app. Please check your inbox for further instructions.",
        "error"
      );
      history.push("/login");
    } else if (
      error.response.data.message === "Invalid session for the user, session is expired."
    ) {
      SendToast("Session expired, please login again.", "error");
      history.push("/login");
    } else if (error.response.data.message.includes("not in the required groups")) {
      setRequestAccessOrPermissionModalType("permission");
      setRequestAccessOrPermissionModalIsShown(true);
    } else {
      setRequestAccessOrPermissionModalType("access");
      setRequestAccessOrPermissionModalIsShown(true);
    }
  };

  const [loginUser] = useMutation(
    (user: MFALoginData) =>
      axiosInstance.post("login/verify", user, { baseURL: `${railAuthApiUrl}/api/` }),
    {
      onSuccess: (response: any) => {
        if (response.data.termsAndCondToken) {
          history.push("/login/terms", response.data);
        } else {
          localStorage.setItem("authenticated", JSON.stringify(response.data.accessToken));
          localStorage.setItem("refreshToken", JSON.stringify(response.data.refreshToken));
          localStorage.setItem("user", JSON.stringify(response.data.user));
          history.push("/");
        }
      },
      onError: onVerifyError,
    }
  );

  const [verifyMFASetup] = useMutation(
    (user: VerifyMFAData) =>
      axiosInstance.post("login/verifyMFASetup", user, { baseURL: `${railAuthApiUrl}/api/` }),
    {
      onSuccess: () => {
        SendToast("Two-Factor Authentication setup is complete. Please login again.", "success");
        verifySetupComplete();
      },
      onError: onVerifyError,
    }
  );

  const onSubmit = async (data: any) => {
    firstLogin
      ? verifyMFASetup({
          userCode: data.code,
          session: session,
        })
      : loginUser({
          userCode: data.code,
          session: loginResponse.session,
          userIdForSRP: loginResponse.userIdForSRP,
          username: loginResponse.username,
        });
  };

  return (
    <div className={styles.login_panel}>
      <div className={firstLogin ? styles.controls : styles.single_control}>
        {firstLogin && (
          <div
            className={styles.control_buttons}
            onClick={() => {
              back();
            }}
          >
            <Icon name="back" size={32} color="#000000" />
          </div>
        )}
        <div
          className={styles.control_buttons}
          onClick={() => {
            close();
          }}
        >
          <Icon name="close" size={32} color="#000000" />
        </div>
      </div>
      <div className={styles.login_form}>
        <div className={styles.login_logo}>
          <img src={loginLogo} alt="Login" style={logoStyle} />
        </div>
        <div className={styles.login_header}>
          <h2 className={styles.login_title} style={headerStyle}>
            Two-Factor Authentication
          </h2>
        </div>
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.form_inputs}>
            <label htmlFor="code">One-Time Passcode</label>
            <Text
              type="body"
              size="16px"
              style={{ marginBottom: "0.7rem", textAlign: "center" }}
              color="#000000"
            >
              {firstLogin
                ? "To complete the set-up of Two-Factor Authentication, enter the one-time passcode from your authenticator app."
                : "To continue logging in, please enter the one-time passcode from your authenticator app."}
            </Text>
            <input
              name="code"
              className={errors.code ? styles.error : ""}
              id="code"
              type="text"
              ref={register({
                required: {
                  value: true,
                  message: "Must not be blank",
                },
              })}
            />
            {errors.code && (
              <Text type="error" style={{ width: "100%", marginTop: "0.5rem" }}>
                {errors.code.message}
              </Text>
            )}
          </div>
          <div className={styles.form_buttons}>
            <Button
              color="blue"
              text="Submit"
              name="login"
              type="submit"
              disabled={!isValid}
              style={{
                borderRadius: "20px",
                justifySelf: "center",
                backgroundColor: isValid ? signInButtonStyle.backgroundColor : "#999999",
                height: "44px",
                width: "100px",
                fontSize: "16px",
                fontWeight: "normal",
                marginTop: "0px",
                marginBottom: "10px",
              }}
            />
          </div>
        </form>
      </div>
      <RequestAccessOrPermissionModal
        show={requestAccessOrPermissionModalIsShown}
        type={requestAccessOrPermissionModalType}
        onClose={() => history.push("/login")}
      />
    </div>
  );
};

export default VerifyLogin;
